"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import { textList, type PageTexts } from "@/lib/siteTexts";

// Wording and links come from Admin -> Тексты страниц (homepage.services.*).
const serviceStyles = [
  { id: "vm", icon: "ServerIcon", color: "#00D4FF" },
  { id: "storage", icon: "CircleStackIcon", color: "#4F7CFF" },
  { id: "network", icon: "GlobeAltIcon", color: "#7C3AED" },
  { id: "billing", icon: "CurrencyDollarIcon", color: "#00FF88" },
  { id: "api", icon: "CodeBracketIcon", color: "#FF6B6B" },
] as const;

type Texts = PageTexts<"homepage">;

function serviceCards(t: Texts) {
  const text = (id: string, field: string) => (t as Record<string, string>)[`services.${id}.${field}`] ?? "";
  return serviceStyles.map((s) => ({
    ...s,
    title: text(s.id, "title"),
    description: text(s.id, "description"),
    href: text(s.id, "href"),
    features: textList(text(s.id, "features")),
    badge: text(s.id, "badge"),
  }));
}

export default function ServicesSection({ texts: t }: { texts: Texts }) {
  const services = serviceCards(t);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 bg-[#050B18] relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute top-1/2 right-0 w-96 h-96 bg-[rgba(79,124,255,0.06)] rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="section-label">{t["services.label"]}</span>
          <h2 className="font-display font-bold text-4xl lg:text-5xl text-white mt-3 mb-4">
            {t["services.title"]}{" "}
            <span className="gradient-text">{t["services.titleAccent"]}</span>
          </h2>
          <p className="text-[#8BA3CC] text-lg max-w-2xl mx-auto">
            {t["services.subtitle"]}
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Large card — VM */}
          <div className="lg:col-span-2 reveal">
            <Link href={services[0].href}>
              <div className="glass rounded-2xl p-8 h-full card-hover neon-border group relative overflow-hidden">
                <div
                  className="absolute top-0 right-0 w-64 h-64 rounded-full blur-[80px] opacity-10 group-hover:opacity-20 transition-opacity"
                  style={{ background: services[0].color }}
                />
                <div className="flex items-start justify-between mb-6">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ background: `${services[0].color}18` }}
                  >
                    <Icon name={services[0].icon} size={28} style={{ color: services[0].color }} />
                  </div>
                  {services[0].badge && (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-[rgba(0,212,255,0.1)] text-[#00D4FF] border border-[rgba(0,212,255,0.2)]">
                      {services[0].badge}
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold text-2xl text-white mb-3">{services[0].title}</h3>
                <p className="text-[#8BA3CC] mb-6 leading-relaxed">{services[0].description}</p>
                <div className="flex flex-wrap gap-2">
                  {services[0].features.map((f) => (
                    <span
                      key={f}
                      className="px-3 py-1 rounded-lg text-xs font-medium text-[#8BA3CC] border border-[rgba(79,124,255,0.15)] bg-[rgba(79,124,255,0.06)]"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm font-semibold" style={{ color: services[0].color }}>
                  {t["services.more"]} <Icon name="ArrowRightIcon" size={16} />
                </div>
              </div>
            </Link>
          </div>

          {/* Medium cards */}
          {services.slice(1, 3).map((service, idx) => (
            <div key={service.id} className={`reveal`} style={{ animationDelay: `${(idx + 1) * 100}ms` }}>
              <Link href={service.href}>
                <div className="glass rounded-2xl p-7 h-full card-hover neon-border-blue group relative overflow-hidden">
                  <div
                    className="absolute top-0 right-0 w-48 h-48 rounded-full blur-[60px] opacity-8 group-hover:opacity-15 transition-opacity"
                    style={{ background: service.color }}
                  />
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: `${service.color}18` }}
                  >
                    <Icon name={service.icon} size={24} style={{ color: service.color }} />
                  </div>
                  <h3 className="font-display font-bold text-xl text-white mb-2">{service.title}</h3>
                  <p className="text-[#8BA3CC] text-sm leading-relaxed mb-4">{service.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {service.features.map((f) => (
                      <span
                        key={f}
                        className="px-2 py-0.5 rounded text-xs text-[#4A6080] border border-[rgba(79,124,255,0.1)]"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </div>
          ))}

          {/* Small cards */}
          {services.slice(3).map((service, idx) => (
            <div key={service.id} className="reveal" style={{ animationDelay: `${(idx + 3) * 100}ms` }}>
              <Link href={service.href}>
                <div className="glass rounded-2xl p-6 card-hover group relative overflow-hidden border border-[rgba(79,124,255,0.1)]">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${service.color}18` }}
                    >
                      <Icon name={service.icon} size={22} style={{ color: service.color }} />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-lg text-white">{service.title}</h3>
                      <p className="text-[#4A6080] text-xs mt-0.5 leading-relaxed">{service.description}</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}